import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { MAKRO_DOKUMENT } from "../makros";
import { pruefeSeite } from "./seite";

export const lektionSeiteEinrichten: Lesson = {
  id: "seite-einrichten",
  title: "Papierformat, Zeilennummern und erste Seite",
  summary: "Drei Einstellungen aus dem Dialog „Seite einrichten“, die über die Ränder hinausgehen.",
  durationMinutes: 8,
  learningGoals: [
    "Das Papierformat wechseln",
    "Zeilennummern für Verträge und Gutachten einschalten",
    "Kopf- und Fußzeile der ersten Seite abweichend gestalten",
  ],
  steps: [
    {
      id: "papierformat",
      title: "Das Papierformat",
      explanation: [
        "„Layout“ → „Format“ wählt das Papierformat. In Deutschland ist A4 Standard; " +
          "Vorlagen aus den USA kommen oft im Format „Letter“ an, das etwas breiter und " +
          "kürzer ist. Ein so gedrucktes Dokument hat am unteren Rand zu wenig Platz oder wird " +
          "vom Drucker skaliert.",
        "Für Flyer und Hefte ist A5 verbreitet. Wer das Format wechselt, prüft danach " +
          "Ränder und Bilder – beides ist in Zentimetern angegeben und passt sich nicht " +
          "automatisch an.",
      ],
      keyPoints: [
        "Layout → Format.",
        "Letter-Vorlagen vor dem Druck auf A4 umstellen.",
        "Nach dem Wechsel Ränder und Bilder prüfen.",
      ],
      task: {
        instruction: "Stelle dein Übungsdokument auf das Papierformat A5 um.",
        hint: "Layout → Format → A5. Danach kannst du es für die weiteren Übungen wieder auf A4 stellen – aber erst nach der Prüfung.",
        macro: MAKRO_DOKUMENT,
        macroNurErsatz: true,
        check: () =>
          pruefeSeite(({ format }) =>
            fromCriteria([criterion(`Papierformat ist A5 (erkannt: ${format ?? "unbekannt"})`, format === "A5")], {
              success: "A5 eingestellt – denk daran, Ränder und Bilder zu prüfen.",
              failure: "Das Dokument steht noch nicht auf A5.",
              hint: "Das Format gilt je Abschnitt. Steht der Cursor in einem anderen Abschnitt, vorher alles markieren (Strg + A).",
            }),
          ),
      },
    },
    {
      id: "zeilennummern",
      title: "Zeilennummern",
      explanation: [
        "Verträge, Gutachten und Drehbücher werden mit Zeilennummern gedruckt, damit man in " +
          "einer Besprechung auf „Seite 3, Zeile 17“ verweisen kann. „Layout“ → " +
          "„Zeilennummern“ schaltet sie ein – fortlaufend, pro Seite neu oder pro Abschnitt " +
          "neu.",
        "Einzelne Absätze, etwa Überschriften, lassen sich ausnehmen: Absatz-Dialog → " +
          "„Zeilen- und Seitenumbruch“ → „Zeilennummern unterdrücken“. Getippte Nummern am " +
          "Zeilenanfang sind keine Alternative – sie wandern mit jeder Änderung an die " +
          "falsche Zeile.",
      ],
      keyPoints: [
        "Layout → Zeilennummern → Fortlaufend / Jede Seite neu beginnen.",
        "Überschriften über den Absatz-Dialog ausnehmen.",
      ],
      task: {
        instruction: "Schalte für dein Dokument die Zeilennummern ein.",
        hint: "Layout → Zeilennummern → „Jede Seite neu beginnen“.",
        macro: MAKRO_DOKUMENT,
        macroNurErsatz: true,
        check: () =>
          pruefeSeite(({ zeilennummern }) =>
            fromCriteria([criterion("Zeilennummern sind eingeschaltet", zeilennummern)], {
              success: "Jetzt lässt sich auf jede Zeile verweisen.",
              failure: "Die Zeilennummern sind noch aus.",
              hint: "Zeilennummern sieht man nur in der Ansicht „Drucklayout“.",
            }),
          ),
      },
    },
    {
      id: "erste-seite-anders",
      title: "Erste Seite anders",
      explanation: [
        "Auf einer Titelseite stören Seitenzahl und Kopfzeile. Doppelklick in die " +
          "Kopfzeile, dann auf der Registerkarte „Kopf- und Fußzeile“ das Häkchen " +
          "„Erste Seite anders“: Die erste Seite bekommt eine eigene, zunächst leere Kopf- " +
          "und Fußzeile.",
        "Daneben steht „Untersch. gerade und ungerade Seiten“ – für doppelseitig gedruckte " +
          "Dokumente, bei denen die Seitenzahl immer außen stehen soll.",
      ],
      keyPoints: [
        "Kopf- und Fußzeile → Erste Seite anders.",
        "Untersch. gerade und ungerade Seiten für den Buchdruck.",
      ],
      task: {
        instruction: "Richte ein, dass die erste Seite eine eigene Kopf- und Fußzeile bekommt.",
        hint: "Doppelklick in die Kopfzeile, Häkchen „Erste Seite anders“.",
        macro: MAKRO_DOKUMENT,
        macroNurErsatz: true,
        check: () =>
          pruefeSeite(({ ersteSeiteAnders }) =>
            fromCriteria([criterion("„Erste Seite anders“ ist eingeschaltet", ersteSeiteAnders)], {
              success: "Die Titelseite bleibt frei von Kopfzeile und Seitenzahl.",
              failure: "„Erste Seite anders“ ist noch nicht gesetzt.",
            }),
          ),
      },
    },
  ],
};
